import { colors } from './colors';
import { pxToRem } from './pxToRem';

const heroCtaSize = {
  fontSize: pxToRem(16),
  lineHeight: pxToRem(24),
  fontWeight: '600',
  height: '56px',
  minH: '56px',
  borderRadius: 'full',
  borderWidth: '0',
  px: 8,
  minW: 'min-content',
};

const size = {
  large: {
    fontSize: pxToRem(15),
    lineHeight: pxToRem(24),
    fontWeight: '600',
    height: '48px',
    borderRadius: '3xl',
    borderWidth: '2px',
  },
  medium: {
    fontSize: pxToRem(14),
    lineHeight: pxToRem(22),
    fontWeight: '600',
    height: '38px',
    borderRadius: '3xl',
    borderWidth: '2px',
  },
};
const type = {
  primary: {
    bg: 'green.G500',
    color: 'white',
    borderColor: 'green.G500',
    _hover: {
      bg: 'green.G700',
      borderColor: 'green.G700',
    },
    _active: {
      bg: 'green.G900',
      borderColor: 'green.G300',
    },
    _disabled: {
      pointerEvents: 'none',
    },
  },
  /** Consumer checkout primary CTA — coral (Figma). Distinct from green {@link primary}. */
  coral: {
    bg: 'coral.C500',
    color: 'white',
    borderColor: 'coral.C500',
    _hover: {
      bg: 'coral.C600',
      borderColor: 'coral.C600',
    },
    _active: {
      bg: 'coral.C700',
      borderColor: 'coral.C700',
    },
    _disabled: {
      pointerEvents: 'none',
    },
  },
  secondary: {
    color: 'green.G500',
    borderColor: 'green.G500',
    _hover: {
      bg: 'green.G0',
      borderColor: 'green.G500',
    },
    _active: {
      bg: 'green.G50',
      color: 'green.G700',
      borderColor: 'green.G700',
    },
  },
  brand: {
    bg: 'yellow.Y500',
    color: 'green.G500',
    borderColor: 'yellow.Y500',
    _hover: {
      bg: 'yellow.Y400',
      borderColor: 'yellow.Y400',
    },
    _active: {
      bg: 'yellow.Y600',
      borderColor: 'yellow.Y600',
    },
  },
  ghost: {
    color: 'green.G500',
    borderColor: 'transparent',
    _hover: { bg: 'green.G0', borderColor: 'green.G0' },
    _active: { bg: 'green.G0', borderColor: 'green.G0', color: 'green.G700' },
  },
  ghostNoPadding: {
    color: 'green.G500',
    borderColor: 'transparent',
    span: {
      svg: {
        stroke: 'green.G500',
      },
    },
    _hover: {
      color: 'green.G700',
      span: {
        svg: {
          stroke: 'green.G700',
        },
      },
    },
    _active: { color: 'green.G900' },
  },
  ghostWhite: {
    color: 'white',
    borderColor: 'transparent',
    _hover: { bg: 'rgba(255, 255, 255, .1)' },
    _active: { bg: 'rgba(255, 255, 255, .2)' },
  },
  ghostWhiteNoPadding: {
    color: 'white',
    borderColor: 'transparent',
    span: {
      svg: {
        stroke: 'white',
      },
    },
    _active: {
      color: 'green.G100',
      span: {
        svg: {
          stroke: 'green.G100',
        },
      },
    },
    _hover: {
      color: 'green.G50',
      span: {
        svg: {
          stroke: 'green.G50',
        },
      },
    },
  },
  /** B2B cream navbar — coral text links (logo / lang pill are separate). */
  ghostB2bNavNoPadding: {
    color: 'b2b.accent',
    borderColor: 'transparent',
    span: {
      svg: {
        stroke: '#E4584A',
      },
    },
    _hover: {
      color: 'b2b.accentHover',
      bg: 'rgba(228, 88, 74, 0.08)',
      span: {
        svg: {
          stroke: '#CF4D40',
        },
      },
    },
    _active: {
      color: 'b2b.accentActive',
    },
  },
  /** Dark pill on green nav — pairs with {@link brand} (yellow login). */
  navB2bRegister: {
    bg: '#0F1E1B',
    color: 'white',
    borderColor: '#0F1E1B',
    _hover: {
      bg: '#182925',
      borderColor: '#182925',
    },
    _active: {
      bg: '#0A1412',
      borderColor: '#0A1412',
    },
  },
  b2bPrimary: {
    bg: 'b2b.accent',
    color: 'white',
    borderColor: 'b2b.accent',
    _hover: {
      bg: 'b2b.accentHover',
      borderColor: 'b2b.accentHover',
    },
    _active: {
      bg: 'b2b.accentActive',
      borderColor: 'b2b.accentActive',
    },
    _disabled: {
      pointerEvents: 'none',
    },
  },
  b2bSecondary: {
    color: 'dark.D800',
    borderColor: 'dark.D200',
    bg: 'white',
    _hover: {
      bg: 'dark.D0',
      borderColor: 'dark.D300',
    },
    _active: {
      bg: 'dark.D100',
      borderColor: 'dark.D400',
    },
  },
  b2bRegistrationPrimary: {
    bg: 'b2b.registrationSubmit',
    color: 'white',
    borderColor: 'b2b.registrationSubmit',
    _hover: {
      bg: 'b2b.registrationSubmitHover',
      borderColor: 'b2b.registrationSubmitHover',
    },
    _active: {
      bg: 'b2b.registrationSubmitActive',
      borderColor: 'b2b.registrationSubmitActive',
    },
    _disabled: {
      pointerEvents: 'none',
    },
  },
  b2bRegistrationSecondary: {
    bg: 'transparent',
    color: 'dark.D800',
    borderColor: 'b2b.registrationCancelBorder',
    _hover: {
      bg: 'rgba(0, 0, 0, 0.04)',
      borderColor: 'dark.D300',
    },
    _active: {
      bg: 'rgba(0, 0, 0, 0.07)',
      borderColor: 'dark.D400',
    },
  },
  /** Plain grey cancel link-style control (no border) on registration footer */
  b2bRegistrationCancelText: {
    bg: 'transparent',
    color: 'dark.D600',
    borderColor: 'transparent',
    borderWidth: '0',
    height: 'auto',
    minH: '44px',
    py: 2,
    px: 3,
    _hover: {
      bg: 'transparent',
      color: 'dark.D800',
    },
    _active: {
      bg: 'transparent',
      color: 'dark.D900',
    },
  },
  /** B2B marketing hero CTA — vertical gradient per landing design */
  b2bLandingHeroPrimary: {
    bgGradient: 'linear(to-b, #FF6B52, #FF8E7A)',
    color: 'white',
    borderColor: 'transparent',
    borderWidth: '0',
    _hover: {
      bgGradient: 'linear(to-b, #ED6049, #E6776E)',
      borderColor: 'transparent',
    },
    _active: {
      bgGradient: 'linear(to-b, #E0563D, #DC6A62)',
      borderColor: 'transparent',
    },
    _disabled: {
      pointerEvents: 'none',
    },
  },
};

export const buttonStyle = {
  primaryLarge: {
    ...size.large,
    ...type.primary,
  },
  primaryMedium: {
    ...size.medium,
    ...type.primary,
  },
  /**
   * Destructive secondary action, mapped from the design system's `Danger` button:
   *
   * | class | here |
   * | --- | --- |
   * | `text-danger` / `border-danger` | `coral.C500` |
   * | `bg-neutral-primary` | `white` |
   * | `hover:bg-danger hover:text-white` | inverts to a filled danger |
   * | `focus:ring-4 focus:ring-neutral-tertiary` | 4px `dark.D150` ring |
   * | `font-medium` / `leading-5` / `text-sm` | 500 / 20px / 14px |
   * | `rounded-base` / `px-4` / `py-2.5` | `base` radius / 16px / 10px |
   *
   * `minH` is the one addition: the spec's padding computes to 42px, just under the 44px minimum for
   * a touch target, and the remove control it replaced was already too small to hit reliably.
   */
  danger: {
    color: 'coral.C500',
    bg: 'white',
    borderColor: 'coral.C500',
    borderWidth: '1px',
    borderRadius: 'base',
    fontSize: pxToRem(14),
    lineHeight: pxToRem(20),
    fontWeight: '500',
    px: '16px',
    py: '10px',
    height: 'auto',
    minH: '44px',
    _hover: {
      bg: 'coral.C500',
      color: 'white',
      borderColor: 'coral.C500',
      _disabled: { bg: 'white', color: 'coral.C500' },
    },
    _active: { bg: 'coral.C600', color: 'white', borderColor: 'coral.C600' },
    _focusVisible: {
      outline: 'none',
      boxShadow: `0 0 0 4px ${colors.dark.D150}`,
    },
    _disabled: { pointerEvents: 'none', opacity: 0.5 },
  },
  /**
   * Consumer checkout purchase CTA — Figma "Ticket Zone Button": a 56px full-width pill with 32px
   * inline padding and no border. Deliberately not built on {@link size.large}, which is 48px with a
   * 24px radius and shared by every other large button in the app.
   */
  coralCta: {
    ...type.coral,
    fontSize: pxToRem(15),
    lineHeight: pxToRem(24),
    fontWeight: '600',
    width: '100%',
    height: '56px',
    borderRadius: '9999px',
    borderWidth: '0',
    px: '32px',
    gap: '8px',
  },
  secondaryLarge: {
    ...size.large,
    ...type.secondary,
  },
  secondaryMedium: {
    ...size.medium,
    ...type.secondary,
  },
  brandLarge: {
    ...size.large,
    ...type.brand,
  },
  brandMedium: {
    ...size.medium,
    ...type.brand,
  },
  ghostLarge: {
    ...size.large,
    ...type.ghost,
  },
  ghostMedium: {
    ...size.medium,
    ...type.ghost,
  },
  ghostNoPaddingLarge: {
    ...size.large,
    ...type.ghostNoPadding,
  },
  ghostNoPaddingMedium: {
    ...size.medium,
    ...type.ghostNoPadding,
  },
  ghostWhiteLarge: {
    ...size.large,
    ...type.ghostWhite,
  },
  ghostWhiteMedium: {
    ...size.medium,
    ...type.ghostWhite,
  },
  ghostWhiteNoPaddingLarge: {
    ...size.large,
    ...type.ghostWhiteNoPadding,
  },
  ghostWhiteNoPaddingMedium: {
    ...size.medium,
    ...type.ghostWhiteNoPadding,
  },
  ghostB2bNavNoPaddingMedium: {
    ...size.medium,
    ...type.ghostB2bNavNoPadding,
  },
  b2bPrimaryLarge: {
    ...size.large,
    ...type.b2bPrimary,
  },
  b2bLandingHeroPrimaryLarge: {
    ...heroCtaSize,
    ...type.b2bLandingHeroPrimary,
  },
  b2bSecondaryLarge: {
    ...size.large,
    ...type.b2bSecondary,
  },
  b2bRegistrationPrimaryLarge: {
    ...size.large,
    ...type.b2bRegistrationPrimary,
  },
  b2bRegistrationSecondaryLarge: {
    ...size.large,
    ...type.b2bRegistrationSecondary,
  },
  b2bRegistrationCancelTextLarge: {
    fontSize: size.large.fontSize,
    lineHeight: size.large.lineHeight,
    fontWeight: size.large.fontWeight,
    borderRadius: size.large.borderRadius,
    ...type.b2bRegistrationCancelText,
  },
  navRegisterEventMedium: {
    ...size.medium,
    ...type.navB2bRegister,
  },
};
